import { CircleNotchIcon, EyeIcon, EyeSlashIcon } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { BrandLogo } from "@/components/brand-logo";
import {
  MAX_PASSWORD_LENGTH,
  MIN_PASSWORD_LENGTH,
  PasswordField,
  passwordProblem,
} from "@/components/password-field";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { apiErrorDetail, apiErrorStatus, useAcceptInvite } from "@/lib/api";
import { PROJECT_NAME } from "@/lib/brand-assets";
import { logger } from "@/lib/logger";
import { retryAfterLabel } from "@/lib/retry-after";

// Constant-length JSX expression keeps prettier output stable for any rendered name.

const TOKEN_PARAM = "token_hash";
const TYPE_PARAM = "type";
const INVITE_TYPE = "invite";

// Why the link can't be used, by the backend's 400 code. Each one replaces the form.
const LINK_PROBLEMS: Record<string, string> = {
  invite_expired:
    "This invite link has expired. Ask your Board of Innovation contact to send you a new one.",
  invite_used:
    "This invite link was already used, or a newer invite replaced it. If you already set your password, sign in. Otherwise, use the link in your most recent invite email.",
  password_rejected:
    "Your invite is accepted, but that password was refused. Choose another one with “Forgot password” on the sign-in page.",
};
const INCOMPLETE_LINK =
  "This invite link is incomplete. Open the link from your invite email again, or ask for a new one.";

/**
 * Where the invite email's link lands: `/accept-invite?token_hash=…&type=invite`.
 * The client chooses a password; the backend spends the link, adds them to their
 * workspace and signs them in, and they land on the portal.
 */
export function AcceptInvitePage() {
  const navigate = useNavigate();
  const acceptMutation = useAcceptInvite();
  const [searchParams, setSearchParams] = useSearchParams();
  // Read once on mount: the effect below strips the token from the address bar.
  const [tokenHash] = useState(() =>
    searchParams.get(TYPE_PARAM) === INVITE_TYPE ? searchParams.get(TOKEN_PARAM) || null : null,
  );
  const [linkProblem, setLinkProblem] = useState(tokenHash === null ? INCOMPLETE_LINK : "");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  // Keep the one-time token out of the history, bookmarks and screenshots.
  useEffect(() => {
    if (searchParams.has(TOKEN_PARAM) || searchParams.has(TYPE_PARAM)) {
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleSubmit = (e: React.SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    const problem = passwordProblem(password, confirmation);
    if (problem) {
      setError(problem);
      return;
    }
    if (tokenHash === null) {
      return;
    }
    logger.debug("[auth] accepting an invite");
    acceptMutation.mutate(
      { tokenHash, password },
      {
        onSuccess: () => {
          logger.debug("[auth] invite accepted → /portal");
          navigate("/portal", { replace: true });
        },
        onError: (err) => {
          const status = apiErrorStatus(err);
          const detail = apiErrorDetail(err);
          logger.debug("[auth] invite accept failed:", status, detail);
          const linkMessage = status === 400 && detail ? LINK_PROBLEMS[detail] : undefined;
          if (linkMessage) {
            setLinkProblem(linkMessage);
          } else {
            setError(acceptErrorMessage(err));
          }
        },
      },
    );
  };

  return (
    <div className="bg-background flex min-h-screen items-center justify-center px-4 py-10">
      <Card variant="box" className="w-full max-w-md">
        <CardHeader className="space-y-6 text-left">
          <p className="text-muted t-eyebrow">Board of Innovation</p>
          <BrandLogo name={PROJECT_NAME} className="h-7 text-2xl" />
          <div className="space-y-2">
            <CardTitle className="t-title text-foreground">Accept your invite</CardTitle>
            <p className="text-muted text-sm leading-6">
              Choose a password of {MIN_PASSWORD_LENGTH} to {MAX_PASSWORD_LENGTH} characters.
              You&apos;ll use it with your email address to sign in.
            </p>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {linkProblem ? (
            <div className="space-y-4">
              <p role="alert" className="text-destructive text-sm leading-6">
                {linkProblem}
              </p>
              <Button
                asChild
                className="bg-accent text-accent-foreground hover:bg-accent-hover h-12 w-full rounded-sm font-semibold"
              >
                <Link to="/login">Go to sign in</Link>
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <PasswordField
                id="newPassword"
                label="Password"
                value={password}
                onChange={setPassword}
                visible={showPassword}
              />
              <PasswordField
                id="confirmPassword"
                label="Confirm password"
                value={confirmation}
                onChange={setConfirmation}
                visible={showPassword}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="text-muted hover:text-foreground flex items-center gap-2 text-xs transition-colors"
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-4 w-4" />
                ) : (
                  <EyeIcon className="h-4 w-4" />
                )}
                {showPassword ? "Hide passwords" : "Show passwords"}
              </button>
              {error && (
                <p role="alert" className="text-destructive text-sm">
                  {error}
                </p>
              )}
              <Button
                type="submit"
                disabled={acceptMutation.isPending}
                className="bg-accent text-accent-foreground hover:bg-accent-hover h-12 w-full rounded-sm font-semibold"
              >
                {acceptMutation.isPending ? (
                  <>
                    <CircleNotchIcon className="h-4 w-4 animate-spin" />
                    Joining…
                  </>
                ) : (
                  "Set password and continue"
                )}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function acceptErrorMessage(err: unknown): string {
  const status = apiErrorStatus(err);
  const detail = apiErrorDetail(err);
  if (status === 403 && detail === "account_disabled") {
    return "This account is deactivated. Contact your Board of Innovation team.";
  }
  if (status === 403) {
    return "Board of Innovation staff sign in with Google and need no invite.";
  }
  if (status === 409) {
    return "This account already belongs to another workspace. Contact your Board of Innovation team.";
  }
  if (status === 429) {
    const wait = retryAfterLabel(err);
    return wait
      ? `Too many attempts. Try again in ${wait}.`
      : "Too many attempts. Try again later.";
  }
  return "Something went wrong. Please try again.";
}
